import { Popover, PopoverContent, PopoverTrigger, RadioGroup, RadioGroupItem, Skeleton } from '@ibsys/kit';
import { EllipsisVerticalIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormGuardActions } from '@/entities/formGuard';
import { DialogsLayout, useSettings } from '@/entities/settings';
import { SkeletonAudio } from '@/features/skeletons';
import { cn } from '@/shared/lib';
import { formGuardStore } from '@/shared/lib/formGuardStore';
import { IconButton, UnsavedChangesDialog } from '@/shared/ui';
import { SkeletonDialog } from './SkeletonDialog';
import { SkeletonMetaDialog } from './SkeletonMetaDialog';
import { SkeletonTable } from './SkeletonTable';
import { SkeletonTableLayout } from './SkeletonTableLayout';

/**
 * Выбор раскладки страницы диалогов. Смена раскладки перемонтирует формы карточки диалога, поэтому при
 * несохранённой правке она применяется только после подтверждения.
 */
export const DialogsSettings = () => {
  const { t } = useTranslation(['dialogs', 'common']);
  const { dialogsPage: dialogsSettings, setDialogsSettings } = useSettings();
  const { resetFormGuard } = useFormGuardActions();
  const layout = dialogsSettings.layout;
  // Раскладка, выбранная при несохранённой правке и ждущая подтверждения
  const [pendingLayout, setPendingLayout] = useState<DialogsLayout | null>(null);

  const handleLayoutChange = (next: DialogsLayout) => {
    if (formGuardStore.getState().isDirty) {
      setPendingLayout(next);
      return;
    }
    setDialogsSettings({ layout: next });
  };

  const handleConfirmLayoutChange = () => {
    if (pendingLayout === null) return;
    resetFormGuard();
    setDialogsSettings({ layout: pendingLayout });
    setPendingLayout(null);
  };

  return (
    <>
      <Popover>
        <IconButton
          icon={<EllipsisVerticalIcon />}
          size={'sm'}
          label={t('dialogs:layoutSettings')}
          trigger={PopoverTrigger}
        />
        <PopoverContent key={layout}>
          <RadioGroup className={'flex flex-col gap-3.5'} value={layout} onChange={handleLayoutChange}>
            <RadioGroupItem value={'one-page'}>
              <div
                className={
                  'w-48 h-24 border-border border rounded-md hover:scale-105  transition-all flex p-1.5 items-center gap-1.5 overflow-hidden'
                }
              >
                <SkeletonTable className={'h-full w-1/3'} />
                <Skeleton className={'h-full w-1/3'} />
                <SkeletonDialog className={'h-full w-1/3'} />
              </div>
            </RadioGroupItem>
            <RadioGroupItem value={'classic'}>
              <div className={'flex gap-1.5 hover:scale-105 hover:translate-x-1 transition-all'}>
                <SkeletonTableLayout />
                <div
                  className={cn('w-48 h-24 border-border border rounded-md p-1.5 items-center gap-1.5 overflow-hidden')}
                >
                  <SkeletonMetaDialog className={'h-full'} />
                </div>
              </div>
            </RadioGroupItem>
            <RadioGroupItem value={'visualizer'}>
              <div className={'flex gap-1.5 hover:scale-105 hover:translate-x-1 transition-all'}>
                <SkeletonTableLayout />
                <div
                  className={cn(
                    'w-48 h-24 border-border border rounded-md flex flex-col p-1.5 items-center gap-1.5 overflow-hidden',
                    '',
                  )}
                >
                  <SkeletonAudio className={'h-4'} />
                  <SkeletonMetaDialog />
                </div>
              </div>
            </RadioGroupItem>
          </RadioGroup>
        </PopoverContent>
      </Popover>
      <UnsavedChangesDialog
        isOpen={pendingLayout !== null}
        onOpenChange={(open) => !open && setPendingLayout(null)}
        onConfirm={handleConfirmLayoutChange}
        title={t('common:unsavedChangesTitle')}
        description={t('dialogs:layoutChangeUnsavedDescription')}
        confirmLabel={t('dialogs:layoutChangeConfirm')}
        cancelLabel={t('common:cancel')}
        className="max-w-md"
      />
    </>
  );
};
